import { useState } from 'react'
import type { ReportsCan, SavedReport, Schedule } from './reportsStore'
import { formatRun, scheduleBadge } from './schedule'
import type { Locale } from './types'

/**
 * "RAPOARTELE MELE": ce s-a salvat pe compania aleasa - ale omului si cele
 * partajate in firma - cu nume, cate blocuri, cand s-a schimbat, daca pleaca
 * pe email; deschide / schimba / redenumeste / copiaza / sterge.
 *
 * "Deschide" arata raportul asa cum e salvat (citire); "Editeaza" il pune in
 * lucru pe ACELASI id - numai al omului si cu `reports.build`, ca si salvarea
 * peste el, pe care serverul o refuza altfel (`not_owner`). Redenumirea si
 * stergerea numai pe ale lui si cu dreptul `reports.build`; stergerea cere o
 * confirmare pe rand, in pagina.
 */

export function MyReports({ reports, schedules, locale, currentId, can, loading = false, error = '', onOpen, onEdit, onRename, onDuplicate, onDelete, onNew }: {
  reports: SavedReport[]
  schedules: Schedule[]
  locale: Locale
  currentId: string | null
  can: ReportsCan
  loading?: boolean
  error?: string
  onOpen(r: SavedReport): void
  onEdit(r: SavedReport): void
  onRename(r: SavedReport, name: string): void
  onDuplicate(r: SavedReport): void
  onDelete(r: SavedReport): void
  onNew(): void
}) {
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const blocks = (n: number) => t(`${n} ${n === 1 ? 'bloc' : 'blocuri'}`, `${n} ${n === 1 ? 'block' : 'blocks'}`)

  return (
    <div className="od-rep-mine">
      <div className="od-rep-section-head">
        <h3>{t('Rapoartele mele', 'My reports')}</h3>
      </div>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {loading ? (
        <p className="od-rep-meta">{t('Se incarca...', 'Loading...')}</p>
      ) : reports.length === 0 ? (
        <div className="od-rep-mine-empty">
          <p>{t('Nu ai inca niciun raport salvat.', 'You have no saved reports yet.')}</p>
          <p className="od-rep-meta">
            {can.build
              ? t('Construieste un raport (sau ia un sablon), apoi apasa "Salveaza".', 'Build a report (or take a template), then press "Save".')
              : t('Rolul tau poate rula si descarca rapoarte, dar nu le poate salva.', 'Your role can run and download reports, but not save them.')}
          </p>
          <button type="button" className="btn btn-primary btn-sm" onClick={onNew}>
            {t('Incepe un raport', 'Start a report')}
          </button>
        </div>
      ) : (
        <ul className="od-rep-mine-list">
          {reports.map((r) => {
            const s = schedules.find((x) => x.report_id === r.id)
            return (
              <li key={r.id} className={`od-rep-mine-row${r.id === currentId ? ' od-rep-mine-current' : ''}`} data-report={r.id}>
                <div className="od-rep-mine-main">
                  {renaming?.id === r.id ? (
                    <form
                      className="od-rep-mine-rename"
                      onSubmit={(e) => {
                        e.preventDefault()
                        if (renaming.name.trim() !== '') onRename(r, renaming.name.trim())
                        setRenaming(null)
                      }}
                    >
                      <input className="form-control input-sm" value={renaming.name} autoFocus aria-label={t('Numele nou', 'New name')} onChange={(e) => setRenaming({ id: r.id, name: e.target.value })} />
                      <button type="submit" className="btn btn-primary btn-sm">
                        {t('Pastreaza', 'Save')}
                      </button>
                      <button type="button" className="btn btn-default btn-sm" onClick={() => setRenaming(null)}>
                        {t('Renunta', 'Cancel')}
                      </button>
                    </form>
                  ) : (
                    <button type="button" className="od-rep-mine-name" onClick={() => onOpen(r)}>
                      {r.name}
                    </button>
                  )}
                  {r.description ? <div className="od-rep-mine-desc">{r.description}</div> : null}
                  <div className="od-rep-mine-meta">
                    <span>{blocks(r.blocks.length)}</span>
                    <span>
                      {t('schimbat', 'modified')} {formatRun(new Date(r.updated_at), locale)}
                    </span>
                    <span>{!r.mine ? t('partajat de un coleg', 'shared by a colleague') : r.visibility === 'company' ? t('vazut de manageri', 'shared with managers') : t('doar eu', 'only me')}</span>
                  </div>
                  {s ? (
                    <span className={`od-rep-badge${s.enabled ? '' : ' od-rep-badge-off'}`}>
                      <span aria-hidden="true">✉</span> {s.enabled ? '' : `${t('Oprit', 'Paused')} · `}
                      {scheduleBadge(s, locale)}
                    </span>
                  ) : null}
                </div>
                <div className="od-rep-mine-actions">
                  {deleting === r.id ? (
                    <span className="od-rep-confirm" role="alert">
                      {t('Stergi raportul?', 'Delete this report?')}
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          setDeleting(null)
                          onDelete(r)
                        }}
                      >
                        {t('Sterge', 'Delete')}
                      </button>
                      <button type="button" className="btn btn-default btn-sm" onClick={() => setDeleting(null)}>
                        {t('Nu', 'No')}
                      </button>
                    </span>
                  ) : (
                    <>
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => onOpen(r)}>
                        {t('Deschide', 'Open')}
                      </button>
                      {can.build && r.mine ? (
                        <button type="button" className="btn btn-default btn-sm od-rep-mine-edit" onClick={() => onEdit(r)}>
                          {t('Editeaza', 'Edit')}
                        </button>
                      ) : null}
                      {can.build && r.mine ? (
                        <button type="button" className="btn btn-default btn-sm" onClick={() => setRenaming({ id: r.id, name: r.name })}>
                          {t('Redenumeste', 'Rename')}
                        </button>
                      ) : null}
                      {can.build ? (
                        <button type="button" className="btn btn-default btn-sm" onClick={() => onDuplicate(r)}>
                          {t('Copiaza', 'Duplicate')}
                        </button>
                      ) : null}
                      {can.build && r.mine ? (
                        <button type="button" className="btn btn-default btn-sm od-rep-danger" onClick={() => setDeleting(r.id)}>
                          {t('Sterge', 'Delete')}
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
